// Enhanced Protocol Management Types with Conditional Logic and Group Processing

import { sampleTypeOptions } from "./SampleTypes";
import { SampleResult } from "./SampleResultTypes";
import {  Sample, SampleAnalysis, statusOptions } from "./SampleTypes";
import type { Node, Edge } from '@xyflow/react';
import type { LegacyUserId } from "../identity";

export interface Protocol {
	id: number;
	name: string;
	version: string;
	description?: string;
	is_first_protocol: boolean;
	test_types?: string[];
	sample_types?: string[];
	is_active: boolean;
	created_by: LegacyUserId;
	created_at: string;
	updated_at: string;
	steps: ProtocolStep[];
	/** React Flow canvas snapshot — present when the protocol was last edited via the flow builder. */
	canvas_data?: ProtocolCanvasData;
	// Computed attributes
	total_duration?: number;
	step_count?: number;
	active_instances_count?: number;
	completion_rate?: number;
}

export interface ProtocolStep {
	id: number;
	protocol_id: number;
	step_number: number;
	name: string;
	description?: string;
	step_type: StepType;
	configuration?: StepConfiguration;
	duration_estimate: number; // in minutes
	method?: ProtocolStepMethod[];
	materials?: ProtocolStepMaterial[];
	notes?: string;

	// Enhanced Conditional Logic Fields
	completion_conditions?: ConditionGroup;
	completion_actions?: ActionConfig[];
	failure_actions?: ActionConfig[];
	timeout_actions?: ActionConfig[];
	timeout_minutes?: number;

	created_at: string;
	updated_at: string;
	// Computed attributes
	required_fields?: StepField[];
	equipment_requirements?: Equipment[];
	conditions?: StepCondition[];
	next_step_rules?: NextStepRule[];
	average_completion_time?: number;
}

export interface ProtocolStepMethod {
	name: string;
	description?: string;
	step_order_number: number;
	created_at: string;
	updated_at: string;
}

export interface ProtocolStepMaterial {
	name: string;
	description?: string;
	quantity: number;
	created_at: string;
	updated_at: string;
}


export interface SampleProtocolInstance {
	id: number;
	sample_id: string;
	protocol_id: number;
	group_id?: number;
	current_step: number;
	status: ProtocolInstanceStatus;
	started_at: string;
	completed_at?: string;
	assigned_to?: LegacyUserId;
	metadata?: Record<string, any>;
	/** Populated when a technician marks this individual sample as failed. */
	failure_reason?: string;
	failed_at?: string;

	// Enhanced Fields
	conditional_data?: Record<string, any>;
	last_action_at?: string;

	created_at: string;
	updated_at: string;
	protocol?: Protocol;
	step_data?: StepInstanceData[];
	sample?: Sample;
	processingGroup?: ProcessingGroup;
	results?: SampleResult[];
	// Computed attributes
	completion_percentage?: number;
	estimated_completion?: string;
	total_processing_time?: number;
}

export interface StepInstanceData {
	id: number;
	instance_id: number;
	step_id: number;
	group_step_instance_id?: number; // NEW: Group step reference
	status: StepInstanceStatus;
	started_at?: string;
	completed_at?: string;
	performed_by?: LegacyUserId;
	field_values?: Record<string, any>;
	notes?: string;
	attachments?: string[];
	created_at: string;
	updated_at: string;
	instance?: SampleProtocolInstance;
	step?: ProtocolStep;

	// Enhanced Fields
	actionLogs?: StepActionLog[];

	// Computed attributes
	duration?: number;
	is_overdue?: boolean;
	time_remaining?: number;
	formatted_field_values?: Record<string, any>;
}

export interface StepActionLog {
	id: number;
	step_instance_id?: number;
	group_step_instance_id?: number; // NEW: Group step reference
	action_type: string;
	action_config: Record<string, any>;
	action_result?: Record<string, any>;
	status: ActionLogStatus;
	error_message?: string;
	executed_by?: LegacyUserId;
	executed_at?: string;
	created_at: string;
	updated_at: string;
	stepInstance?: StepInstanceData;
	groupStepInstance?: GroupStepInstance; // NEW: Group step relationship
}

export interface AnalysisProtocolInstance {
	id: number;
	analysis_id: number;
	protocol_id: number;
	group_id?: number;
	current_step: number;
	status: ProtocolInstanceStatus;
	started_at?: string;
	completed_at?: string;
	assigned_to?: LegacyUserId;
	metadata?: Record<string, any>;
	/** Populated when a technician marks this individual analysis as failed. */
	failure_reason?: string;
	failed_at?: string;

	created_at: string;
	updated_at: string;

	// Relationships
	protocol?: Protocol;
	processing_group?: ProcessingGroup;
	step_data?: StepInstanceData[];
	analysis?: SampleAnalysis;

	// Computed attributes
	completion_percentage?: number;
	estimated_completion?: string;
	total_processing_time?: number;
}



// ===== GROUP PROCESSING TYPES =====

export interface ProcessingGroup {
	id: number;
	group_name: string;
	description?: string;
	protocol_id: number;
	status: GroupProcessingStatus;
	/** Legacy — last single active step. Kept for backward compat. */
	current_step: number;
	/** Branch-aware: all currently-open step numbers (one per active branch). */
	active_step_numbers?: number[];
	/**
	 * Maps instance ID (string key) → the step_number that instance is
	 * currently being processed at. Updated by the auto-execution engine.
	 */
	branch_state?: Record<string, number>;
	started_at?: string;
	completed_at?: string;
	created_by: LegacyUserId;
	assigned_to?: LegacyUserId;
	metadata?: Record<string, any>;
	created_at: string;
	updated_at: string;

	// Relationships
	protocol?: Protocol;
	sample_instances?: SampleProtocolInstance[];
	analysis_instances?: AnalysisProtocolInstance[];
	group_step_instances?: GroupStepInstance[];
	samples?: any[];
	results?: SampleResult[];

	// Computed attributes
	completion_percentage?: number;
	sample_count?: number;
	estimated_completion?: string;
}

export interface GroupStepInstance {
	id: number;
	group_id: number;
	step_id: number;
	status: StepInstanceStatus;
	started_at?: string;
	completed_at?: string;
	performed_by?: LegacyUserId;
	field_values?: Record<string, any>;
	/**
	 * Per-instance field values entered by the technician.
	 * Key: instance ID (stringified number). Value: field_values map for that instance.
	 */
	per_sample_field_values?: Record<string, Record<string, any>>;
	/**
	 * Routing result set by the auto-execution engine for conditional nodes.
	 * Key: instance ID (string). Value: 'condition_yes' | 'condition_no'.
	 */
	sample_routing?: Record<string, 'condition_yes' | 'condition_no'>;
	/**
	 * Instance IDs that this step applies to. Null means all active instances.
	 * Set by the auto-execution engine when routing branches.
	 */
	applicable_instance_ids?: number[] | null;
	notes?: string;
	attachments?: string[];
	created_at: string;
	updated_at: string;

	// Relationships
	group?: ProcessingGroup;
	step?: ProtocolStep;
	actionLogs?: GroupStepActionLog[];
	individualStepInstances?: StepInstanceData[];

	// Computed attributes
	duration?: number;
}

export interface GroupStepActionLog {
	id: number;
	group_step_instance_id: number;
	action_type: string;
	action_config: Record<string, any>;
	action_result?: Record<string, any>;
	status: ActionLogStatus;
	error_message?: string;
	executed_by?: LegacyUserId;
	executed_at?: string;
	created_at: string;
	updated_at: string;
	groupStepInstance?: GroupStepInstance;
}

// Enhanced Conditional Logic Types
export interface ConditionGroup {
	operator: 'AND' | 'OR';
	conditions: (Condition | ConditionGroup)[];
}

export interface Condition {
	field: string;
	operator: ConditionOperator;
	value: any;
	source?: 'field' | 'instance' | 'sample' | 'protocol';
}

export interface ActionConfig {
	type: ActionType;
	config: Record<string, any>;
	critical?: boolean;
}

// Action Type Definition for Documentation
export interface ActionTypeDefinition {
	name: string;
	description: string;
	available: boolean;
	config_schema: Record<string, {
		type: string;
		required: boolean;
		default?: any;
		enum?: string[];
		source?: string;
		description?: string;
	}>;
	examples?: Record<string, any>[];
}

// Operator Definition for Documentation
export interface OperatorDefinition {
	name: string;
	description: string;
	data_types: string[];
	example: Condition;
}

// Sample Processing Enhancement Types
export interface SampleTimeline {
	sample_id: string;
	timeline: TimelineEvent[];
}

export interface TimelineEvent {
	timestamp: string;
	type: 'step' | 'action';
	protocol_name?: string;
	step_name?: string;
	step_status?: string;
	performed_by?: LegacyUserId;
	actions_executed?: number;
	action_type?: string;
	status?: string;
	result?: Record<string, any>;
	error?: string;
}

// Configuration Types (Enhanced)
export interface StepConfiguration {
	required_fields?: StepField[];
	equipment_requirements?: Equipment[];
	conditions?: StepCondition[];
	next_steps?: NextStepRule[];
	/** Actions executed when an automated node is reached. Replaces the old completion_actions / failure_actions split for automated steps. */
	automation_actions?: ActionConfig[];
}

export interface StepField {
	field_name: string;
	field_type: FieldType;
	required: boolean;
	validation_rules?: ValidationRules;
	default_value?: any;
}

export interface Equipment {
	type: string;
	name: string;
}

export interface StepCondition {
	field: string;
	operator: ConditionOperator;
	value: any;
	action?: string;
}

export interface NextStepRule {
	condition?: StepCondition;
	next_step: number;
	/** Which edge handle this rule came from; used by the execution engine for conditional routing. */
	connection_type?: ProtocolFlowEdgeType;
}

export interface ValidationRules {
	min?: number;
	max?: number;
	min_length?: number;
	max_length?: number;
	pattern?: string;
	in?: string[];
	decimal_places?: number;
	allowed_extensions?: string[];
	max_file_size?: number;
	multi_select?: boolean;
	min_date?: string;
	max_date?: string;
	format?: string;
	[key: string]: any;
}

// Statistics and Monitoring Types
export interface ActionStatistics {
	total_actions: number;
	successful_actions: number;
	failed_actions: number;
	pending_actions: number;
	actions_by_type: Record<string, number>;
	actions_by_status: Record<string, number>;
	recent_failures: StepActionLog[];
	success_rate: number;
}

export interface ProtocolStatistics {
	total_instances: number;
	active_instances: number;
	completed_instances: number;
	failed_instances: number;
	on_hold_instances: number;
	completion_rate: number;
	average_processing_time: number;
	step_performance: StepPerformance[];
}

export interface StepPerformance {
	step_id: number;
	step_number: number;
	step_name: string;
	estimated_duration: number;
	average_duration: number;
	completion_rate: number;
}

export interface InstanceStatistics {
	total_instances: number;
	active_instances: number;
	completed_instances: number;
	failed_instances: number;
	on_hold_instances: number;
	average_completion_time: number;
}

// NEW: Group Processing Statistics
export interface GroupProcessingStatistics {
	total_groups: number;
	active_groups: number;
	completed_groups: number;
	failed_groups: number;
	on_hold_groups: number;
	pending_groups: number;
	groups_by_protocol: Record<string, number>;
	average_samples_per_group: number;
}

// Paginated Response Types
export interface PaginatedProtocolResponse {
	data: Protocol[];
	total: number;
	per_page: number;
	current_page: number;
	last_page: number;
	from: number;
	to: number;
}

export interface PaginatedInstanceResponse {
	data: SampleProtocolInstance[];
	total: number;
	per_page: number;
	current_page: number;
	last_page: number;
	from: number;
	to: number;
}

export interface PaginatedActionLogsResponse {
	data: StepActionLog[];
	total: number;
	per_page: number;
	current_page: number;
	last_page: number;
	from: number;
	to: number;
}

// NEW: Group Processing Paginated Response
export interface PaginatedProcessingGroupResponse {
	data: ProcessingGroup[];
	total: number;
	per_page: number;
	current_page: number;
	last_page: number;
	from: number;
	to: number;
}

// Request Types

export interface CompatibleProtocolsRequest {
	test_type: string;
	sample_type: string;
}

export interface CloneProtocolRequest {
	new_version: string;
	new_name?: string;
}


// NEW: Group Processing Request Types
export interface CreateProcessingGroupRequest {
	group_name: string;
	description?: string;
	protocol_id: number;
	sample_ids?: string[];
	analysis_ids?: number[];
	created_by: LegacyUserId;
	assigned_to?: LegacyUserId;
}

export interface StartGroupProcessingRequest {
	performed_by: LegacyUserId;
}

export interface CompleteGroupStepRequest {
	/** ID of the ProtocolStep being completed. */
	step_id: number;
	field_values: Record<string, any>;
	/** Per-instance field values keyed by instance ID (string). */
	per_sample_field_values?: Record<string, Record<string, any>>;
	performed_by: LegacyUserId;
	notes?: string;
	attachments?: string[];
	/** Restricts completion to a subset of instances (branch support). */
	applicable_instance_ids?: number[];
}

export interface SaveGroupStepDraftRequest {
	step_id: number;
	field_values: Record<string, any>;
	per_sample_field_values?: Record<string, Record<string, any>>;
	performed_by: LegacyUserId;
	notes?: string;
}

export interface FailInstanceRequest {
	reason: string;
	performed_by: LegacyUserId;
	/** 'sample' | 'analysis' — helps the backend skip one query. */
	instance_type?: 'sample' | 'analysis';
}

export interface StartGroupStepRequest {
	step_number: number;
	performed_by: LegacyUserId;
}

export interface AddSamplesToGroupRequest {
	sample_ids: string[];
	performed_by: LegacyUserId;
}

export interface HoldGroupRequest {
	reason?: string;
}

export interface FailGroupRequest {
	reason?: string;
}

// Testing and Validation Request Types
export interface TestConditionRequest {
	condition: ConditionGroup;
	test_data: {
		field_values?: Record<string, any>;
		instance_data?: Record<string, any>;
	};
}

export interface TestActionRequest {
	action: ActionConfig;
	step_instance_id?: number;
	context_data?: Record<string, any>;
	dry_run?: boolean;
}

export interface ValidateActionConfigRequest {
	action_type: string;
	config: Record<string, any>;
}

// Filter and Query Types (Enhanced)
export interface ProtocolFilter {
	active_only?: boolean;
	test_type?: string;
	sample_type?: string;
	search?: string;
	sort_by?: string;
	sort_order?: 'asc' | 'desc';
	per_page?: number;
	page?: number;
}


export interface StatisticsFilter {
	date_from?: string;
	date_to?: string;
	action_type?: string;
	status?: string;
}

// Enhanced Filter Types
export interface ActionLogFilter {
	action_type?: string;
	status?: ActionLogStatus;
	sample_id?: string;
	protocol_id?: number;
	date_from?: string;
	date_to?: string;
	per_page?: number;
	page?: number;
	sort_by?: string;
	sort_order?: 'asc' | 'desc';
}

export interface BulkRetryActionRequest {
	action_log_ids?: number[];
	action_types?: string[];
	step_instance_ids?: number[];
	performed_by: LegacyUserId;
}

// NEW: Group Processing Filter Types
export interface GroupProcessingFilter {
	status?: GroupProcessingStatus;
	protocol_id?: number;
	assigned_to?: LegacyUserId;
	created_by?: LegacyUserId;
	sort_by?: string;
	sort_order?: 'asc' | 'desc';
	per_page?: number;
	page?: number;
}

// Enums (Enhanced)
export type StepType =
	| 'manual'
	| 'automated'
	| 'qc'
	| 'results'
	| 'aliquot'
	| 'conditional'
	| 'start'
	| 'end';

export type ProtocolInstanceStatus = 'pending' | 'active' | 'completed' | 'failed' | 'on_hold';

// NEW: Group Processing Status
export type GroupProcessingStatus = 'pending' | 'active' | 'completed' | 'failed' | 'on_hold';

export type StepInstanceStatus =
	| 'pending'
	| 'in_progress'
	| 'completed'
	| 'failed'
	| 'skipped'
	| 'requires_rework'
	| 'timed_out';

export type FieldType = 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'dropdown' | 'file';

export type ConditionOperator =
	| '=' | '==' | '!='
	| '>' | '>=' | '<' | '<='
	| 'contains' | 'not_contains' | 'starts_with' | 'ends_with'
	| 'in' | 'not_in' | 'contains_any' | 'contains_all'
	| 'includes' | 'excludes' | 'equals_exactly'
	| 'regex' | 'is_empty' | 'is_not_empty' | 'range';

export type ActionLogStatus = 'pending' | 'executing' | 'completed' | 'failed';

export type ActionType =
	| 'update_sample_status'
	| 'update_analysis_status'
	| 'update_protocol_status'
	| 'update_lab_order_status'
	| 'send_notification'
	| 'create_quality_check'
	| 'schedule_follow_up'
	| 'generate_report'
	| 'create_audit_log'
	| 'update_batch_status'
	| 'update_priority'
	| 'conditional_branch';

// Options for Dropdowns (Enhanced)
export const StepTypeOptions = [
	{ label: "Manual", value: "manual" },
	{ label: "Automated", value: "automated" },
	{ label: "Sample Aliquoting", value: "aliquot" },
	{ label: "Quality Control", value: "qc" },
	{ label: "Results", value: "results" }
];

export const FieldTypeOptions = [
	{ label: "Text", value: "text" },
	{ label: "Number", value: "number" },
	{ label: "Date", value: "date" },
	{ label: "Date & Time", value: "datetime" },
	{ label: "Boolean", value: "boolean" },
	{ label: "Dropdown", value: "dropdown" },
	{ label: "File", value: "file" },
];

export const ProtocolInstanceStatusOptions = [
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" },
	{ label: "On Hold", value: "on_hold" }
];

// NEW: Group Processing Status Options
export const GroupProcessingStatusOptions = [
	{ label: "Pending", value: "pending" },
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" },
	{ label: "On Hold", value: "on_hold" }
];


export const EquipmentTypeOptions = [
	{ label: "Centrifuge", value: "centrifuge" },
	{ label: "Thermocycler", value: "thermocycler" },
	{ label: "Spectrophotometer", value: "spectrophotometer" },
	{ label: "DNA/RNA Extractor", value: "extractor" },
	{ label: "Array Scanner", value: "array_scanner" },
	{ label: "DNA Sequencer", value: "sequencer" },
	{ label: "Microscope", value: "microscope" },
	{ label: "Incubator", value: "incubator" },
	{ label: "Freezer", value: "freezer" },
	{ label: "Thermometer", value: "thermometer" },
	{ label: "Analytical Balance", value: "balance" },
	{ label: "Pipette", value: "pipette" },
	{ label: "Vortex Mixer", value: "vortex" },
	{ label: "Other Equipment", value: "other" }
];

// Default values (Enhanced)
export const defaultProtocol: Partial<Protocol> = {
	name: '',
	version: '1.0',
	description: '',
	test_types: [],
	sample_types: [],
	is_active: true,
	steps: []
};

export const defaultProtocolStep: Partial<ProtocolStep> = {
	name: '',
	description: '',
	step_type: undefined,
	duration_estimate: 60,
	configuration: {
		required_fields: [],
		equipment_requirements: []
	},
	completion_conditions: undefined,
	completion_actions: [],
	failure_actions: [],
	timeout_actions: [],
	timeout_minutes: undefined
};

export const defaultStepField: Partial<StepField> = {
	field_name: '',
	field_type: 'text',
	required: false,
	validation_rules: {}
};

// ─────────────────────────────────────────────────────────────────────────────
// Conditional Node Routing Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Category grouping for the condition type catalog shown in ConditionalNodeEditor.
 */
export type ProtocolConditionCategory =
	| 'step_outcome'
	| 'field_value'
	| 'qc_result'
	| 'sample_data'
	| 'results_data';

/**
 * All concrete condition types a Conditional node can evaluate.
 * These are checked automatically by the backend execution engine when the
 * protocol run reaches a conditional node — no user interaction required.
 */
export type ProtocolConditionType =
	// Step outcome: did an upstream step complete/fail/time out?
	| 'step_completed'
	| 'step_failed'
	| 'step_timed_out'
	| 'step_duration_exceeded'
	// Field value: compare a captured field from an upstream step
	| 'field_value'
	// QC results: did an upstream QC step pass or fail?
	| 'qc_passed'
	| 'qc_failed'
	| 'qc_metric_value'
	// Sample metadata (available from protocol start)
	| 'sample_status'
	| 'sample_type'
	| 'sample_priority'

/**
 * One row inside a ConditionalNodeConfig's conditions array.
 * step_node_id is preserved by the frontend for canvas restore.
 * step_number_ref is resolved at save-time so the backend can evaluate without canvas_data.
 */
export interface ConditionalNodeCondition {
	id: string;
	type: ProtocolConditionType;
	/** Canvas node ID of the referenced upstream step (frontend only). */
	step_node_id?: string;
	/** Step number resolved at save-time (used by backend execution engine). */
	step_number_ref?: number;
	/** Field key within the step's captured field_values (for field_value / qc_metric_value / result_value). */
	field_key?: string;
	operator?: ConditionOperator;
	value?: any;
	/** Human-readable summary auto-generated by the editor for display. */
	label?: string;
}

/**
 * The full conditional routing configuration stored inside a conditional node's
 * configuration.conditional_config JSON field.
 */
export interface ConditionalNodeConfig {
	group_operator: 'AND' | 'OR';
	conditions: ConditionalNodeCondition[];
	/**
	 * Whether the conditions are evaluated once per-group (all instances must pass)
	 * or once per-sample (each instance is routed independently).
	 * Default: 'per_group'
	 */
	evaluation_scope?: 'per_group' | 'per_sample';
	/** Custom label shown on the Yes edge handle (default: "Yes"). */
	yes_label?: string;
	/** Custom label shown on the No edge handle (default: "No"). */
	no_label?: string;
}

/**
 * Static catalog describing each condition type.
 * Mirrors the backend GET /conditional-logic/conditions/types endpoint.
 */
export const PROTOCOL_CONDITION_CATALOG: Record<
	ProtocolConditionType,
	{
		category: ProtocolConditionCategory;
		label: string;
		description: string;
		requires_step_ref: boolean;
		requires_field_ref: boolean;
		value_type: 'none' | 'string' | 'number' | 'boolean' | 'enum';
		/** Operators shown in the operator dropdown for this condition type. */
		operators: ConditionOperator[];
		/** For enum value_type — provide options to show in the value select. */
		enum_options?: { value: string; label: string }[];
	}
> = {
	// ── Step outcome ─────────────────────────────────────────────────────────
	step_completed: {
		category: 'step_outcome',
		label: 'Step Completed',
		description: 'True when the referenced upstream step has been completed successfully',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'none',
		operators: [],
	},
	step_failed: {
		category: 'step_outcome',
		label: 'Step Failed',
		description: 'True when the referenced upstream step ended with a failure status',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'none',
		operators: [],
	},
	step_timed_out: {
		category: 'step_outcome',
		label: 'Step Timed Out',
		description: 'True when the referenced upstream step exceeded its timeout window',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'none',
		operators: [],
	},
	step_duration_exceeded: {
		category: 'step_outcome',
		label: 'Step Duration Exceeded',
		description: 'True when the actual processing time of the step exceeded a threshold',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'number',
		operators: ['>', '>=', '<', '<='],
	},
	// ── Field value ──────────────────────────────────────────────────────────
	field_value: {
		category: 'field_value',
		label: 'Captured Field Value',
		description: 'Compare a data field captured during an upstream step against a target value',
		requires_step_ref: true,
		requires_field_ref: true,
		value_type: 'string',
		operators: ['=', '!=', '>', '>=', '<', '<=', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty', 'range'],
	},
	// ── QC result ────────────────────────────────────────────────────────────
	qc_passed: {
		category: 'qc_result',
		label: 'QC Step Passed',
		description: 'True when all quality control checks in the referenced QC step passed',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'none',
		operators: [],
	},
	qc_failed: {
		category: 'qc_result',
		label: 'QC Step Failed',
		description: 'True when any quality control check in the referenced QC step failed',
		requires_step_ref: true,
		requires_field_ref: false,
		value_type: 'none',
		operators: [],
	},
	qc_metric_value: {
		category: 'qc_result',
		label: 'QC Metric Value',
		description: 'Compare a specific QC metric captured during a QC step against a threshold',
		requires_step_ref: true,
		requires_field_ref: true,
		value_type: 'number',
		operators: ['=', '!=', '>', '>=', '<', '<=', 'range'],
	},
	// ── Sample data ──────────────────────────────────────────────────────────
	sample_status: {
		category: 'sample_data',
		label: 'Sample Status',
		description: 'Compare the current processing status of the sample',
		requires_step_ref: false,
		requires_field_ref: false,
		value_type: 'enum',
		operators: ['=', '!='],
		enum_options: statusOptions,
	},
	sample_type: {
		category: 'sample_data',
		label: 'Sample Type',
		description: 'Check the type of the sample being processed (e.g. blood, saliva, tissue)',
		requires_step_ref: false,
		requires_field_ref: false,
		value_type: 'enum',
		operators: ['=', '!='],
		enum_options: sampleTypeOptions
	},	
	sample_priority: {
		category: 'sample_data',
		label: 'Sample Priority',
		description: 'Compare the numeric priority level of the sample (1 = lowest, 10 = highest)',
		requires_step_ref: false,
		requires_field_ref: false,
		value_type: 'number',
		operators: ['=', '!=', '>', '>=', '<', '<='],
	},

};

/** Grouping metadata for the condition type picker UI. */
export const CONDITION_CATEGORY_META: Record<
	ProtocolConditionCategory,
	{ label: string; icon: string; color: string }
> = {
	step_outcome:  { label: 'Step Outcome',  icon: 'CheckCircleOutlined',   color: '#10b981' },
	field_value:   { label: 'Field Value',   icon: 'DatabaseOutlined',       color: '#3b82f6' },
	qc_result:     { label: 'QC Result',     icon: 'SafetyCertificateOutlined', color: '#f59e0b' },
	sample_data:   { label: 'Sample Data',   icon: 'ExperimentOutlined',     color: '#8b5cf6' },
	results_data:  { label: 'Results Data',  icon: 'BarChartOutlined',       color: '#ec4899' },
};

// ─────────────────────────────────────────────────────────────────────────────
// React Flow Canvas Types
// ─────────────────────────────────────────────────────────────────────────────

export type ProtocolFlowEdgeType = 'default' | 'condition_yes' | 'condition_no';

/** The data payload stored inside every ProtocolFlowNode. */
export interface ProtocolFlowNodeData extends Record<string, unknown> {
	step_type: StepType;
	label: string;
	description?: string;
	duration_estimate?: number;
	/** Assigned at save time via BFS topological sort. */
	step_number?: number;
	is_entry?: boolean;
	// All ProtocolStep sub-data (mirrored for direct use in sub-editors)
	method?: ProtocolStepMethod[];
	materials?: ProtocolStepMaterial[];
	configuration?: StepConfiguration;
	completion_conditions?: ConditionGroup;
	completion_actions?: ActionConfig[];
	failure_actions?: ActionConfig[];
	timeout_actions?: ActionConfig[];
	timeout_minutes?: number;
	notes?: string;
	/** Routing configuration for conditional nodes — stored in configuration.conditional_config on save. */
	conditional_config?: ConditionalNodeConfig;
}

export type ProtocolFlowNode = Node<ProtocolFlowNodeData>;

export interface ProtocolFlowEdge extends Edge {
	data?: {
		connection_type: ProtocolFlowEdgeType;
	};
}

export interface ProtocolCanvasData {
	nodes: ProtocolFlowNode[];
	edges: ProtocolFlowEdge[];
	viewport?: { x: number; y: number; zoom: number };
}

/** Visual configuration for each node type used in the protocol builder canvas. */
export const PROTOCOL_NODE_CONFIG: Record<StepType, { label: string; color: string; description: string }> = {
	start:       { label: 'Start',            color: '#10b981', description: 'Entry point of the protocol workflow' },
	manual:      { label: 'Manual Step',      color: '#3b82f6', description: 'Requires manual lab technician action' },
	automated:   { label: 'Automated Step',   color: '#0ea5e9', description: 'Automated instrument or processing step' },
	qc:          { label: 'QC Check',         color: '#f59e0b', description: 'Quality control checkpoint' },
	results:     { label: 'Results',          color: '#8b5cf6', description: 'Record, upload, or capture results' },
	aliquot:     { label: 'Aliquot',          color: '#06b6d4', description: 'Sample aliquoting and labelling step' },
	conditional: { label: 'Conditional',      color: '#ec4899', description: 'Branch the workflow based on a condition' },
	end:         { label: 'End',              color: '#ef4444', description: 'Protocol completion / termination point' },
};

/**
 * Assigns a logical step number (1-based BFS depth) to every protocol flow node.
 * start/entry nodes receive step 1; downstream nodes increment from there.
 * Mirrors computeStageNumbers from TreatmentPlanTypes.
 */
export function computeProtocolStepNumbers(
	nodes: ProtocolFlowNode[],
	edges: ProtocolFlowEdge[],
): Map<string, number> {
	const result = new Map<string, number>();
	const inDegree = new Map<string, number>();
	const adjacency = new Map<string, string[]>();

	for (const n of nodes) {
		inDegree.set(n.id, 0);
		adjacency.set(n.id, []);
	}
	for (const e of edges) {
		inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1);
		adjacency.get(e.source)?.push(e.target);
	}

	// Seed queue with nodes that have no incoming edges
	const queue: string[] = [];
	for (const [id, deg] of inDegree) {
		if (deg === 0) queue.push(id);
	}

	// Kahn's BFS topological sort — each node gets its own unique step_number.
	// Processing one node at a time (not the whole level at once) ensures that
	// branch nodes (e.g. both sides of a conditional) receive different numbers
	// instead of colliding on the same step_number, which would cause the
	// backend keyBy('step_number') to drop one of them.
	let stepNum = 1;
	while (queue.length) {
		const nodeId = queue.shift()!;
		result.set(nodeId, stepNum++);
		for (const neighbour of adjacency.get(nodeId) ?? []) {
			const newDeg = (inDegree.get(neighbour) ?? 0) - 1;
			inDegree.set(neighbour, newDeg);
			if (newDeg === 0) queue.push(neighbour);
		}
	}

	return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Conditional node editor — operator display labels & field-type operator sets
// ─────────────────────────────────────────────────────────────────────────────

/** Human-readable label for every ConditionOperator, used in dropdowns. */
export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
	'=':             '= equals',
	'==':            '= equals',
	'!=':            '≠ not equals',
	'>':             '> greater than',
	'>=':            '≥ greater or equal',
	'<':             '< less than',
	'<=':            '≤ less or equal',
	contains:        'contains',
	not_contains:    'does not contain',
	starts_with:     'starts with',
	ends_with:       'ends with',
	in:              'is one of',
	not_in:          'is not one of',
	contains_any:    'has any of (any selected)',
	contains_all:    'has all of (all selected)',
	includes:        'includes value',
	excludes:        'does not include',
	equals_exactly:  'is exactly',
	regex:           'matches regex',
	is_empty:        'is empty',
	is_not_empty:    'is not empty',
	range:           'is between (range)',
};

/** Operators that are valid for each field type in the condition builder. */
export const FIELD_TYPE_OPERATORS: Record<string, ConditionOperator[]> = {
	text:     ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with', 'regex', 'is_empty', 'is_not_empty'],
	number:   ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
	boolean:  ['=', '!='],
	dropdown: ['includes', 'excludes', 'equals_exactly', 'contains_any', 'contains_all', '=', '!=', 'in', 'not_in', 'is_empty', 'is_not_empty'],
	date:     ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
	datetime: ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
	file:     ['is_empty', 'is_not_empty'],
};

// ─────────────────────────────────────────────────────────────────────────────
// Aliquot step configuration constants
// ─────────────────────────────────────────────────────────────────────────────

export const ALIQUOT_CONTAINER_TYPES: { value: string; label: string }[] = [
	{ value: '1.5ml_tube',     label: '1.5 mL Tube' },
	{ value: '0.5ml_tube',     label: '0.5 mL Tube' },
	{ value: '0.2ml_pcr_tube', label: '0.2 mL PCR Tube' },
	{ value: '2.0ml_tube',     label: '2.0 mL Tube' },
	{ value: 'cryovial',       label: 'Cryovial' },
	{ value: '96_well_plate',  label: '96-Well Plate' },
	{ value: 'custom',         label: 'Custom' },
];

export const ALIQUOT_STORAGE_TEMPERATURES: { value: string; label: string }[] = [
	{ value: 'RT',   label: 'Room Temperature (RT)' },
	{ value: '4C',   label: '4°C (Refrigerator)' },
	{ value: '-20C', label: '-20°C (Freezer)' },
	{ value: '-80C', label: '-80°C (Ultra-Low)' },
	{ value: 'LN2',  label: 'LN₂ (Liquid Nitrogen)' },
];

export const ALIQUOT_LABEL_VARS: { value: string; label: string }[] = [
	{ value: '{{incremental}}', label: '{{incremental}} — #1, #2…' },
	{ value: '{{analysis}}',    label: '{{analysis}} — Full name' },
	{ value: '{{analysis_F}}',  label: '{{analysis_F}} — Short code' },
	{ value: '{{date}}',        label: '{{date}} — YYYYMMDD' },
	{ value: '{{time}}',        label: '{{time}} — HHMMSS' },
	{ value: '{{sample_id}}',   label: '{{sample_id}} — Sample ID' },
];
